'use client'
import * as React from 'react'
import { InputContainer, inputContainerVariants } from './input-container'
import { InputBase } from './input-base'
import { Button, type ButtonVariants } from '../button'
import { Typography } from '../typography'
import type { VariantProps } from 'class-variance-authority'
import { Icon } from '../icon'
import { cn } from '../../lib/utils'
import gsap from 'gsap'

export interface InputNumericStepperProps
  extends React.InputHTMLAttributes<HTMLInputElement>,
    VariantProps<typeof inputContainerVariants> {
  min?: number
  max?: number
  step?: number
  'data-error'?: boolean
}

const InputNumericStepper = React.forwardRef<
  HTMLInputElement,
  InputNumericStepperProps
>(
  (
    {
      variant,
      inputSize,
      value,
      min,
      max,
      step = 1,
      onChange,
      disabled,
      className,
      placeholder,
      ...props
    },
    ref,
  ) => {
    const sizeKey = inputSize ?? 'md'
    const [isFocused, setIsFocused] = React.useState(false)
    const [displayValue, setDisplayValue] = React.useState(value)
    const [direction, setDirection] = React.useState<'up' | 'down'>('up')
    const numberRef = React.useRef<HTMLSpanElement>(null)
    const prevValueRef = React.useRef(value)

    React.useEffect(() => {
      if (value === prevValueRef.current) return
      setDirection(Number(value) > Number(prevValueRef.current) ? 'up' : 'down')
      setDisplayValue(prevValueRef.current) // Start with previous value
      if (numberRef.current) {
        gsap.fromTo(
          numberRef.current,
          { y: 0, opacity: 1 },
          {
            y: direction === 'up' ? -24 : 24,
            opacity: 0,
            duration: 0.2,
            onComplete: () => {
              setDisplayValue(value) // Switch to new value
              if (numberRef.current) {
                gsap.fromTo(
                  numberRef.current,
                  { y: direction === 'up' ? 24 : -24, opacity: 0 },
                  { y: 0, opacity: 1, duration: 0.2 },
                )
              }
            },
          },
        )
      } else {
        setDisplayValue(value)
      }
      prevValueRef.current = value
    }, [direction, value])

    const wrapperSizeVariants: Record<
      'sm' | 'md' | 'lg',
      ButtonVariants['size']
    > = {
      sm: 'xs',
      md: 'sm',
      lg: 'md',
    }

    const handleDecrement = () => {
      if (disabled) return
      const newValue =
        typeof min === 'number'
          ? Math.max(convertToNumber(value) - step, min)
          : convertToNumber(value) - step
      if (onChange) {
        onChange({
          target: {
            value: newValue.toString(),
            name: props.name,
          },
        } as React.ChangeEvent<HTMLInputElement>)
      }
    }

    const handleIncrement = () => {
      if (disabled) return
      const newValue =
        typeof max === 'number'
          ? Math.min(convertToNumber(value) + step, max)
          : convertToNumber(value) + step
      if (onChange) {
        onChange({
          target: {
            value: newValue.toString(),
            name: props.name,
          },
        } as React.ChangeEvent<HTMLInputElement>)
      }
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value
      if (val === '') {
        if (onChange) {
          onChange({
            target: {
              value: ''.toString(),
              name: props.name,
            },
          } as React.ChangeEvent<HTMLInputElement>)
        }
        return
      }
      const num = Number(val)
      if (!isNaN(num)) {
        if (typeof min === 'number' && num < min) return
        if (typeof max === 'number' && num > max) return
        if (onChange) {
          onChange({
            target: {
              value: num.toString(),
              name: props.name,
            },
          } as React.ChangeEvent<HTMLInputElement>)
        }
      }
    }

    const convertToNumber = (
      value: number | string | undefined | readonly string[],
    ) => {
      return Number(String(value)?.replace(/,/g, ''))
    }

    return (
      <InputContainer
        variant={variant}
        inputSize={inputSize}
        data-error={props['data-error']}
        data-focused={isFocused}
        className={cn('!p-2xs', className)}
      >
        <div className='relative w-full flex items-center justify-center'>
          <Button
            type='button'
            onClick={handleIncrement}
            disabled={
              disabled ||
              (typeof max === 'number' && convertToNumber(value) >= max)
            }
            tabIndex={-1}
            variant='ghost'
            color='neutral'
            size={wrapperSizeVariants[sizeKey]}
            aria-label='Increment'
            startIcon={<Icon name='PlusLine' size='parent' />}
          />

          {/* Visually hidden input for accessibility and form */}
          <InputBase
            {...props}
            type='text'
            inputMode='numeric'
            value={convertToNumber(value)}
            onChange={handleInputChange}
            className={cn(
              'text-center',
              isFocused ? 'text-inherit' : 'text-transparent ',
            )}
            ref={ref}
            tabIndex={-1}
            onFocus={(e) => {
              setIsFocused(true)
              props.onFocus?.(e as React.FocusEvent<HTMLInputElement>)
            }}
            onBlur={(e) => {
              setIsFocused(false)
              props.onBlur?.(e as React.FocusEvent<HTMLInputElement>)
            }}
          />
          {/* Animated number */}
          {!isFocused && (
            <span
              ref={numberRef}
              className='block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-center select-none pointer-events-none text-inherit'
              style={{ minWidth: 32 }}
              aria-hidden='true'
            >
              {displayValue === 0 ||
              displayValue === '' ||
              displayValue === '0' ? (
                <Typography
                  size='sm'
                  color='text-neutral-surface-60'
                  className='text-center'
                >
                  {placeholder}
                </Typography>
              ) : (
                displayValue
              )}
            </span>
          )}

          <Button
            type='button'
            onClick={handleDecrement}
            disabled={
              disabled ||
              (typeof min === 'number' && convertToNumber(value) <= min)
            }
            tabIndex={-1}
            variant='ghost'
            color='neutral'
            size={wrapperSizeVariants[sizeKey]}
            aria-label='Decrement'
            startIcon={<Icon name='MinusLine' size='parent' />}
          />
        </div>
      </InputContainer>
    )
  },
)

InputNumericStepper.displayName = 'InputNumericStepper'

export { InputNumericStepper }
